<!-- src/views/new-tip-v2/Dashboard.vue -->
<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <h1 class="text-h5 font-weight-medium">Dashboard New TIP v2</h1>
      <v-spacer />
      <v-btn outlined :loading="exporting" @click="onExport">
        <v-icon left>mdi-microsoft-excel</v-icon>
        Export Excel
      </v-btn>
    </div>

    <new-tip-v2-filters-bar
      v-model="localFilters"
      :campaigns="campaigns"
      :supervisors="supervisors"
      @apply="onFiltersApply"
    />

    <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Visites soumises" icon="mdi-clipboard-check"
          :value="visitsSoFar"
          :hint="`sur ${maxPossibleVisits} possibles`"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Avancement" icon="mdi-progress-check"
          :value="kpis ? kpis.progression_rate : 0" is-percentage
          :hint="`${kpis ? kpis.salepoints_visited : 0} / ${kpis ? kpis.salepoints_total : 0} POS visités`"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Total gains" icon="mdi-cash" icon-color="success"
          :value="kpis ? kpis.total_gain : 0" suffix="pts"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Récompensés Gifty" icon="mdi-gift" icon-color="purple"
          :value="kpis ? kpis.visits_rewarded : 0"
          :hint="`sur ${kpis ? kpis.visits_validated : 0} validées`"
          :loading="loading"
        />
      </v-col>
    </v-row>

    <v-row dense class="mt-1">
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Gains de paliers" :value="kpis ? kpis.gain_breakdown.objective : 0"
          suffix="pts" hint="hors chapeau" :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Gains chapeau" :value="kpis ? kpis.gain_breakdown.hats : 0"
          suffix="pts" hint="cumulés par-dessus" :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Stock moyen relevé" :value="kpis ? kpis.avg_stock_qty : 0"
          :hint="`${kpis ? formatNumber(kpis.total_stock_qty) : 0} au total`"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Visites sans palier" icon="mdi-close-circle-outline" icon-color="grey"
          :value="kpis ? kpis.tiers_distribution.none : 0"
          hint="aucun objectif franchi" :loading="loading"
        />
      </v-col>
    </v-row>

    <v-card outlined class="mt-4" v-if="kpis">
      <v-card-title class="text-subtitle-1">
        Répartition des visites par palier atteint
      </v-card-title>
      <v-card-text>
        <bar-chart :chart-data="chartData" :chart-options="chartOptions" :height="80" />
      </v-card-text>
    </v-card>

    <v-card outlined class="mt-4">
      <v-card-title class="text-subtitle-1">Détail des visites</v-card-title>
      <v-divider />
      <v-data-table
        :headers="headers"
        :items="visits"
        :loading="loading"
        :server-items-length="meta.total"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        @update:options="onOptionsChange"
      >
        <template #item.campaign="{ item }">
          <div class="text-caption">{{ item.campaign ? item.campaign.name : '—' }}</div>
          <div class="text-caption grey--text" v-if="item.campaign && item.campaign.product">
            {{ (item.campaign.product.name || '').trim() }}
          </div>
        </template>
        <template #item.supervisor="{ item }">
          <div>{{ item.supervisor ? item.supervisor.name : '—' }}</div>
          <div class="text-caption grey--text">{{ item.supervisor ? item.supervisor.phone : '' }}</div>
        </template>
        <template #item.salepoint="{ item }">
          <div>{{ item.salepoint ? item.salepoint.name : '—' }}</div>
          <div class="text-caption grey--text">{{ item.salepoint ? item.salepoint.code : '' }}</div>
        </template>
        <template #item.submitted_at="{ item }">{{ formatDateTime(item.submitted_at) }}</template>
        <template #item.stock_qty="{ item }">{{ formatNumber(item.stock_qty) }}</template>
        <template #item.achieved_tier="{ item }">
          <v-chip x-small label outlined :color="tierColor(item.achieved_tier)">
            {{ tierLabel(item.achieved_tier) }}
          </v-chip>
        </template>
        <template #item.total_gain="{ item }">
          <strong :class="item.total_gain > 0 ? 'success--text' : 'grey--text'">
            {{ formatNumber(item.total_gain) }}
          </strong>
        </template>
        <template #item.status="{ item }">
          <new-tip-status-badge type="visit" :value="item.status" />
        </template>
        <template #item.rewarded="{ item }">
          <v-chip x-small label outlined v-if="item.rewarded" color="purple">
            <v-icon left x-small>mdi-check</v-icon> Payé
          </v-chip>
          <v-chip x-small label outlined v-else-if="item.status === 'validated'" color="orange">
            En attente
          </v-chip>
          <span v-else class="text-caption grey--text">—</span>
        </template>
        <template #item.actions="{ item }">
          <v-btn icon small :to="{ name: 'new-tip-v2.visits.show', params: { id: item.id } }">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatDateTime, formatNumber } from '@/helpers/formatters';
import { tierLabel, tierColor, MAX_VISITS_PER_ASSIGNMENT } from '@/constants/newTipV2';
// Composants présentationnels partagés avec la v1.
import KpiCard from '@/components/new-tip/KpiCard.vue';
import StatusBadge from '@/components/new-tip/StatusBadge.vue';
import FiltersBar from '@/components/new-tip-v2/FiltersBar.vue';
import { Bar as BarChart } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import api from '@/api/newTipV2Api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
  name: 'NewTipV2Dashboard',
  components: {
    'new-tip-kpi-card': KpiCard,
    'new-tip-status-badge': StatusBadge,
    'new-tip-v2-filters-bar': FiltersBar,
    BarChart,
  },
  data() {
    return {
      localFilters: {},
      options: { page: 1, itemsPerPage: 25 },
      exporting: false,
      campaigns: [],
      supervisors: [],
      headers: [
        { text: 'Campagne', value: 'campaign', sortable: false },
        { text: 'ASM', value: 'supervisor', sortable: false },
        { text: 'POS', value: 'salepoint', sortable: false },
        { text: 'Date', value: 'submitted_at' },
        { text: 'Stock', value: 'stock_qty', align: 'right', sortable: false },
        { text: 'Palier', value: 'achieved_tier', sortable: false },
        { text: 'Total', value: 'total_gain', align: 'right' },
        { text: 'Statut', value: 'status', sortable: false },
        { text: 'Gifty', value: 'rewarded', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    };
  },
  computed: {
    ...mapState('newTipV2', {
      kpis: s => s.dashboard.kpis,
      visits: s => s.dashboard.visits,
      meta: s => s.dashboard.meta,
      filters: s => s.dashboardFilters,
    }),
    loading() { return this.$store.getters['newTipV2/isLoading']('dashboard'); },
    visitsSoFar() {
      if (!this.kpis) return 0;
      return this.kpis.visits_validated + this.kpis.visits_submitted + this.kpis.visits_rejected;
    },
    maxPossibleVisits() {
      if (!this.kpis) return 0;
      return this.kpis.salepoints_total * MAX_VISITS_PER_ASSIGNMENT;
    },
    chartData() {
      if (!this.kpis) return { labels: [], datasets: [] };
      const d = this.kpis.tiers_distribution;
      return {
        labels: ['Aucun palier', 'Palier 1', 'Palier 2', 'Palier 3'],
        datasets: [{
          label: 'Visites',
          data: [d.none, d.tier_1, d.tier_2, d.tier_3],
          backgroundColor: ['#9E9E9E', '#F57C00', '#1976D2', '#388E3C'],
          // Sans cela, 4 catégories seulement donnent des barres très larges.
          barPercentage: 0.5,
          categoryPercentage: 0.6,
        }],
      };
    },
  },
  methods: {
    ...mapActions('newTipV2', ['fetchDashboard', 'exportDashboard']),
    formatDateTime,
    formatNumber,
    tierLabel,
    tierColor,
    onFiltersApply(filters) {
      this.$store.commit('newTipV2/SET_DASHBOARD_FILTERS', filters);
      this.options.page = 1;
      this.reload();
    },
    onOptionsChange() { this.reload(); },
    async reload() {
      await this.fetchDashboard({
        ...this.filters,
        page: this.options.page,
        per_page: this.options.itemsPerPage,
      });
    },
    async onExport() {
      this.exporting = true;
      try { await this.exportDashboard(); } finally { this.exporting = false; }
    },
    async fetchOptions() {
      try {
        const [campaignsRes, supervisorsRes] = await Promise.all([
          api.listCampaigns({ per_page: 100 }),
          api.listSupervisors(),
        ]);
        this.campaigns = campaignsRes.data.data || [];
        this.supervisors = (supervisorsRes.data && supervisorsRes.data.data) || [];
      } catch (err) {
        console.error(err);
      }
    },
  },
  async mounted() {
    // Le store est persisté (vuex-persistedstate) : sans remise à zéro, les
    // filtres d'une visite précédente — un statut « Soumise » par exemple —
    // resteraient appliqués au rechargement. On repart donc toujours de
    // filtres vides, puis on applique ceux de l'URL.
    this.$store.commit('newTipV2/RESET_DASHBOARD_FILTERS');

    // Filtres passés dans l'URL : depuis le détail d'une campagne
    // (?campaign_id=…) ou depuis le raccourci « Visites à valider »
    // du menu latéral (?status=submitted).
    const fromQuery = {};
    ['campaign_id', 'supervisor_id', 'salepoint_id'].forEach((key) => {
      if (this.$route.query[key]) fromQuery[key] = Number(this.$route.query[key]);
    });
    ['status', 'achieved_tier', 'date_from', 'date_to', 'salepoint_code'].forEach((key) => {
      if (this.$route.query[key]) fromQuery[key] = this.$route.query[key];
    });
    if (Object.keys(fromQuery).length) {
      this.$store.commit('newTipV2/SET_DASHBOARD_FILTERS', fromQuery);
    }

    // Les campagnes doivent être chargées avant de pouvoir en présélectionner une.
    await this.fetchOptions();

    // À défaut de campagne dans l'URL, on se place sur la plus récente : un
    // dashboard toutes campagnes confondues mélange des grilles d'objectifs
    // différentes et n'est pas lisible.
    if (!this.filters.campaign_id && this.campaigns.length) {
      this.$store.commit('newTipV2/SET_DASHBOARD_FILTERS', {
        campaign_id: this.campaigns[0].id,
      });
    }

    this.localFilters = { ...this.filters };
    this.reload();
  },
};
</script>
